import '@skyscanner-internal/backpack-web/bpk-stylesheets/base';
import '@skyscanner-internal/backpack-web/bpk-stylesheets/base.css';
import '@skyscanner-internal/backpack-web/bpk-stylesheets/font';
import '@skyscanner-internal/backpack-web/bpk-stylesheets/larken';

import { setLoginFormData } from '@web-platform/shared-acorn-utils-frontend/src/singletons/globalLoginFormData';
import { setupCoreWebVitalsMonitoring } from '@web-platform/shared-banana-tracking/src/trackCoreWebVitals/setupCoreWebVitalsMonitoring';
import { setupViewportHeight } from '@web-platform/shared-banana-utils-frontend/src/helpers/setupViewportHeight';
import { setInternalLinkingGcData } from '@web-platform/shared-banana-utils-frontend/src/singletons/seoComponents/internalLinkingGcData';
import { setInternationalSites } from '@web-platform/shared-banana-utils-frontend/src/singletons/seoComponents/internationalSites';
import { CONFIG_SERVICE_KEYS } from '@web-platform/shared-universal-constants-config';
import { loggedInStatusInitialized } from '@web-platform/shared-universal-redux/src/actions/user';
import { getConfigServiceValue } from '@web-platform/shared-universal-utils-frontend/src/config';
import {
  isAcornType,
  isAcornRequest,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/isAcornRequest';
import {
  logError,
  logWarn,
} from '@web-platform/shared-universal-utils-frontend/src/helpers/logger';
import { historyLocation } from '@web-platform/shared-universal-utils-frontend/src/history';
import { setGcCultureSelectorData } from '@web-platform/shared-universal-utils-frontend/src/singletons/cultureSelectorData';
import { setFooterData } from '@web-platform/shared-universal-utils-frontend/src/singletons/footerData';
import { setHeaderData } from '@web-platform/shared-universal-utils-frontend/src/singletons/globalHeaderData';
import { setSubscriptionClient } from '@web-platform/shared-universal-utils-frontend/src/singletons/subscriptionClient';
import {
  setCldr,
  setCulture,
  setFullDomainName,
} from '@web-platform/shared-universal-utils-frontend-localisation';
import { createRoot } from 'react-dom/client';
import { GLOBAL } from 'saddlebag-browser';
import { isLoggedIn } from 'saddlebag-user-management';

import { App } from './App';
import { emitAdSponsoredSearchEvent } from './emitAdSponsoredSearchEvent';
import { emitSearchEvent } from './emitSearchEvent';
import { setupErrorHandling } from './error-handling';
import { getContextAndHandleError } from './getContextAndHandleError';
import { hydrateContext } from './hydrateContext';
import { setupStore } from './redux/setup-store';

import type {
  AcornInitData,
  AcornWindowData,
  BananaInitData,
  UniversalWindowData,
} from '@web-platform/shared-universal-types/src/context';
import type { BananaStore } from '@web-platform/shared-universal-types/src/redux/state/state-shared-types';

setupErrorHandling();

export const getIsLoggedInFromSaddlebag = async (): Promise<boolean> => {
  try {
    return await isLoggedIn();
  } catch (error) {
    logError({
      error,
      message: 'Error checking logged in status',
      component: 'SessionValidation',
    });
    return false;
  }
};

const getAcornInitData = (): AcornWindowData => {
  const $window = GLOBAL.getWindow();
  const { footerComponentData, loginFormComponentData } = $window.__internal;
  return {
    footerComponentData,
    loginFormComponentData,
  };
};

const getUniversalInitData = (): UniversalWindowData => {
  const $window = GLOBAL.getWindow();
  const { cldr, culture, fullDomainName, headerComponentData, viewId } = $window.__internal;
  return { cldr, culture, fullDomainName, headerComponentData, viewId };
};

export const getInitData = async (): Promise<AcornInitData | BananaInitData | null> => {
  const universalInitData = getUniversalInitData();

  if (isAcornRequest()) {
    return { ...getAcornInitData(), ...universalInitData };
  }

  const bananaContext = await getContextAndHandleError();
  if (!bananaContext) return null;
  return { ...bananaContext, ...universalInitData };
};

// to initialize Banana-specific settings
const baseBananaSetup = ({ gcCultureSelectorData, seoComponents }: BananaInitData) => {
  setGcCultureSelectorData(gcCultureSelectorData);
  setSubscriptionClient();

  if (seoComponents) {
    const { internalLinkingGc, internationalSites } = seoComponents;
    setInternationalSites(internationalSites);

    if (internalLinkingGc) {
      setInternalLinkingGcData(internalLinkingGc);
    }
  }
};

// to initialize Acorn-specific settings
const baseAcornSetup = ({ loginFormComponentData }: AcornInitData) => {
  setLoginFormData(loginFormComponentData);
  setSubscriptionClient();
};

const emitAcornSearchEvent = (
  store: Awaited<ReturnType<typeof setupStore>>,
  viewId: string,
): void => {
  try {
    emitSearchEvent(store, viewId);
  } catch (error) {
    logWarn({
      component: 'emitSearchEvent',
      message: `Search event emission failed: ${error}`,
    });
  }
};

const emitAcornAdSponsoredSearchEvent = (viewId: string): void => {
  if (GLOBAL.getWindow().__internal?.sponsoredContent) {
    try {
      emitAdSponsoredSearchEvent(viewId);
    } catch (error) {
      logWarn({
        component: 'emitAdSponsoredSearchEvent',
        message: `Ad sponsored search event emission failed: ${error}`,
      });
    }
  }
};

/*
  We need to make sure the execution order of these code
  1. Set Culture - set the culture for the app
  2. Setup Store - the network observer need to get culture
  3. Setup Core Web Vitals Monitoring - create tracking and get state from store
*/
const run = async () => {
  const initData = await getInitData();
  if (!initData) return;

  const { cldr, culture, footerComponentData, fullDomainName, headerComponentData } = initData;

  setCldr(cldr);
  setCulture(culture);
  setFullDomainName(fullDomainName);

  setFooterData(footerComponentData);
  setHeaderData(headerComponentData);

  try {
    const { initTrackingPreferences } = await import('saddlebag-tracking-preferences');
    initTrackingPreferences({
      enableKetchCMP: Boolean(getConfigServiceValue(CONFIG_SERVICE_KEYS.OPS.ENABLE_KETCH_CMP)),
      consumer: 'banana',
      market: culture.market,
      ketchScriptUrl: getConfigServiceValue(CONFIG_SERVICE_KEYS.OPS.KETCH_SCRIPT_URL),
      ketchConsentCookieName: getConfigServiceValue(
        CONFIG_SERVICE_KEYS.OPS.KETCH_CONSENT_COOKIE_NAME,
      ),
      ketchTCFStorageKey: getConfigServiceValue(CONFIG_SERVICE_KEYS.OPS.KETCH_TCF_STORAGE_KEY),
      enableCookieVersionRollback: Boolean(
        getConfigServiceValue(CONFIG_SERVICE_KEYS.OPS.ENABLE_COOKIE_VERSION_ROLLBACK),
      ),
      enablePurposeScopedIdService: Boolean(
        getConfigServiceValue(CONFIG_SERVICE_KEYS.OPS.ENABLE_PURPOSE_SCOPED_ID_SERVICE),
      ),
      enablePurposeScopedIdExistingGDPRUsers: Boolean(
        getConfigServiceValue(CONFIG_SERVICE_KEYS.OPS.ENABLE_PURPOSE_SCOPED_ID_EXISTING_GDPR_USERS),
      ),
      enablePurposeScopedIdNonRegulatedMarkets: Boolean(
        getConfigServiceValue(
          CONFIG_SERVICE_KEYS.OPS.ENABLE_PURPOSE_SCOPED_ID_NON_REGULATED_MARKETS,
        ),
      ),
      purposeScopedIdServiceLegacyCmpUrl: getConfigServiceValue(
        CONFIG_SERVICE_KEYS.OPS.PURPOSE_SCOPED_ID_SERVICE_LEGACY_CMP_URL,
      ),
      enableConsentIdService: Boolean(
        getConfigServiceValue(CONFIG_SERVICE_KEYS.OPS.ENABLE_CONSENT_ID_SERVICE),
      ),
      enableKetchPsidSync: Boolean(
        getConfigServiceValue(CONFIG_SERVICE_KEYS.OPS.ENABLE_KETCH_PSID_SYNC),
      ),
      enableLegacyConsentSync: Boolean(
        getConfigServiceValue(CONFIG_SERVICE_KEYS.OPS.ENABLE_LEGACY_CONSENT_SYNC),
      ),
      consentIdServiceUrl: getConfigServiceValue(CONFIG_SERVICE_KEYS.OPS.CONSENT_ID_SERVICE_URL),
      purposeScopedIdServiceKetchCmpUrl: getConfigServiceValue(
        CONFIG_SERVICE_KEYS.OPS.PURPOSE_SCOPED_ID_SERVICE_KETCH_CMP_URL,
      ),
    });
  } catch (error) {
    logWarn({
      component: 'initTrackingPreferences',
      error: error instanceof Error ? error.toString() : String(error),
      message: 'Failed to initialise tracking preferences',
    });
  }

  const $document = GLOBAL.getDocument();
  const appRootElement = $document.getElementById('app-root');

  if (isAcornType<AcornInitData, BananaInitData>(initData)) {
    baseAcornSetup(initData);

    const store = await setupStore();

    emitAcornSearchEvent(store, initData.viewId);
    emitAcornAdSponsoredSearchEvent(initData.viewId);

    $document.getElementById('global-header')?.focus();
    await getIsLoggedInFromSaddlebag();
    const root = createRoot(appRootElement);
    root.render(<App store={store} />);
  } else {
    baseBananaSetup(initData);
    // @ts-expect-error: Type 'StoreType' is not assignable to type 'BananaStore'.
    const store: BananaStore = await setupStore();

    setupViewportHeight();
    setupCoreWebVitalsMonitoring();

    const isLoggedInFromSaddlebag = await getIsLoggedInFromSaddlebag();
    await hydrateContext(store, initData, historyLocation);
    store.dispatch(loggedInStatusInitialized(isLoggedInFromSaddlebag));

    const root = createRoot(appRootElement);
    root.render(<App store={store} />);
  }
};

export { run };
